/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { inject, Injectable, signal } from '@angular/core';
import { AlfrescoApiService } from '@alfresco/adf-content-services';
import { NodesApi } from '@alfresco/js-api';
import { forkJoin, from, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import {
  DEFAULT_MIGRATION_SETTINGS,
  MigrationAuditEntry,
  MigrationConnection,
  MigrationJob,
  MigrationRun,
  MigrationRunIndexEntry,
  MigrationSettings
} from './migration.models';
import { ConnectorRegistry } from './connector-registry.service';

/**
 * Where the migration center keeps its records — plain JSON documents under
 * `Data Dictionary/DMS Migration`, the same pattern DMS Jobs and the menu
 * policies already use:
 *
 *   connections.json   saved connections (NEVER secrets — see saveConnection)
 *   settings.json      deployment-wide migration settings
 *   audit.json         the append-mostly audit trail (capped, newest first)
 *   jobs/<name>.json   one migration definition per file (drafts and runs)
 *   runs/<name>.json   run reports — written by the migration service, read here
 *
 * Everything in Data Dictionary is readable by GROUP_EVERYONE, which is why
 * connection secrets are stripped before a connection is saved — the service
 * side owns them once a backend exists.
 */
@Injectable({ providedIn: 'root' })
export class MigrationService {
  private readonly api = inject(AlfrescoApiService);
  private readonly registry = inject(ConnectorRegistry);

  private static readonly ROOT = 'Data Dictionary/DMS Migration';
  private static readonly JOBS = `${MigrationService.ROOT}/Jobs`;
  private static readonly RUNS = `${MigrationService.ROOT}/Runs`;
  private static readonly CONNECTIONS = `${MigrationService.ROOT}/connections.json`;
  private static readonly SETTINGS = `${MigrationService.ROOT}/settings.json`;
  private static readonly AUDIT = `${MigrationService.ROOT}/audit.json`;
  private static readonly AUDIT_CAP = 500;

  private get nodes(): NodesApi {
    return new NodesApi(this.api.getInstance() as any);
  }

  /**
   * A draft the wizard should reopen — set by the jobs table's Edit action
   * before navigating to the New Migration tab; consumed on wizard init.
   */
  readonly pendingDraft = signal<MigrationJob | null>(null);

  // ── connections ───────────────────────────────────────────────────────

  connections(): Observable<MigrationConnection[]> {
    return this.readJson<{ connections?: MigrationConnection[] }>(MigrationService.CONNECTIONS).pipe(
      map((doc) => (Array.isArray(doc?.connections) ? doc.connections : [])),
      catchError(() => of([] as MigrationConnection[]))
    );
  }

  /**
   * Upserts a connection. `config` arrives with whatever the form held —
   * fields the connector marks `secret` are dropped here, never reaching the
   * repository document. The connection's id is stable (name-derived slug +
   * timestamp on create) so jobs can reference it.
   */
  saveConnection(connection: MigrationConnection): Observable<MigrationConnection> {
    const secretKeys = new Set((this.registry.get(connection.connectorId)?.fields ?? []).filter((f) => f.secret).map((f) => f.key));
    const clean: MigrationConnection = {
      ...connection,
      config: Object.fromEntries(Object.entries(connection.config ?? {}).filter(([k]) => !secretKeys.has(k)))
    };

    return this.connections().pipe(
      map((all) => {
        const rest = all.filter((c) => c.id !== clean.id);
        return [...rest, clean];
      }),
      switchMap((all) => this.writeJson(MigrationService.CONNECTIONS, { connections: all }).pipe(map(() => clean))),
      switchMap((saved) => this.audit('connection.saved', { connectionId: saved.id, detail: saved.name }).pipe(map(() => saved)))
    );
  }

  removeConnection(id: string): Observable<void> {
    return this.connections().pipe(
      map((all) => all.filter((c) => c.id !== id)),
      switchMap((all) => this.writeJson(MigrationService.CONNECTIONS, { connections: all })),
      switchMap(() => this.audit('connection.deleted', { connectionId: id })),
      map(() => undefined)
    );
  }

  // ── jobs ──────────────────────────────────────────────────────────────

  jobs(): Observable<MigrationJob[]> {
    return this.listFolderJson<MigrationJob>(MigrationService.JOBS);
  }

  /** Creates the definition file or overwrites it; returns the node id. */
  saveJob(job: MigrationJob): Observable<string> {
    const { id, ...def } = job;
    const json = JSON.stringify(def, null, 2);

    if (id) {
      return from(this.nodes.updateNodeContent(id, json) as Promise<any>).pipe(
        switchMap(() => this.audit('migration.saved', { jobId: id, detail: job.name })),
        map(() => id)
      );
    }

    return this.folderId(MigrationService.JOBS).pipe(
      switchMap((folderId) =>
        from(this.nodes.createNode(folderId, { name: this.fileName(job.name), nodeType: 'cm:content' } as any) as Promise<any>)
      ),
      switchMap((res: any) =>
        from(this.nodes.updateNodeContent(res.entry.id, json) as Promise<any>).pipe(
          switchMap(() => this.audit('migration.created', { jobId: res.entry.id, detail: job.name })),
          map(() => res.entry.id as string)
        )
      )
    );
  }

  removeJob(id: string): Observable<void> {
    return from(this.nodes.deleteNode(id, { permanent: true }) as Promise<any>).pipe(
      switchMap(() => this.audit('migration.deleted', { jobId: id })),
      map(() => undefined)
    );
  }

  // ── runs (written by the migration service, read by this page) ────────

  private static readonly RUN_INDEX = `${MigrationService.RUNS}/index.json`;

  runs(maxItems = 50): Observable<MigrationRun[]> {
    return this.listFolderJson<MigrationRun>(MigrationService.RUNS, maxItems).pipe(
      // the run index lives beside the reports — it has no jobId, so it drops here
      map((runs) => runs.filter((r) => !!r?.jobId))
    );
  }

  /** Slim per-run summaries the engine maintains — cheap overviews without
   * downloading every full report. Empty when the engine predates the index. */
  runIndex(): Observable<MigrationRunIndexEntry[]> {
    return this.readJson<{ entries?: MigrationRunIndexEntry[] }>(MigrationService.RUN_INDEX).pipe(
      map((doc) => (Array.isArray(doc?.entries) ? doc.entries : [])),
      catchError(() => of([] as MigrationRunIndexEntry[]))
    );
  }

  /** One full run report by node id — for screens that need per-item detail. */
  report(nodeId: string): Observable<MigrationRun | null> {
    return this.readText(nodeId).pipe(
      map((text) => {
        try {
          const parsed = JSON.parse(text) as MigrationRun;
          parsed.nodeId = nodeId;
          return parsed;
        } catch {
          return null;
        }
      }),
      catchError(() => of(null))
    );
  }

  // ── settings ──────────────────────────────────────────────────────────

  settings(): Observable<MigrationSettings> {
    return this.readJson<MigrationSettings>(MigrationService.SETTINGS).pipe(
      map((doc) => ({ ...DEFAULT_MIGRATION_SETTINGS, ...(doc ?? {}) })),
      catchError(() => of({ ...DEFAULT_MIGRATION_SETTINGS }))
    );
  }

  saveSettings(settings: MigrationSettings): Observable<void> {
    return this.writeJson(MigrationService.SETTINGS, settings).pipe(
      switchMap(() => this.audit('settings.saved', {})),
      map(() => undefined)
    );
  }

  // ── audit ─────────────────────────────────────────────────────────────

  auditEntries(): Observable<MigrationAuditEntry[]> {
    return this.readJson<{ entries?: MigrationAuditEntry[] }>(MigrationService.AUDIT).pipe(
      map((doc) => (Array.isArray(doc?.entries) ? doc.entries : [])),
      catchError(() => of([] as MigrationAuditEntry[]))
    );
  }

  /**
   * Appends one line to the audit trail. Read-modify-write on a single JSON
   * document — capped so the file stays small; newest entries first.
   * Failures are swallowed: audit must never block the action it records.
   */
  audit(action: string, extra: { jobId?: string; connectionId?: string; detail?: string; result?: string } = {}): Observable<void> {
    const user = this.currentUser();
    const entry: MigrationAuditEntry = { at: new Date().toISOString(), user, action, ...extra };
    return this.auditEntries().pipe(
      map((entries) => [entry, ...entries].slice(0, MigrationService.AUDIT_CAP)),
      switchMap((entries) => this.writeJson(MigrationService.AUDIT, { entries })),
      catchError(() => of(undefined)),
      map(() => undefined)
    );
  }

  private currentUser(): string {
    try {
      const instance: any = this.api.getInstance();
      return instance?.getEcmUsername?.() || 'unknown';
    } catch {
      return 'unknown';
    }
  }

  // ── plumbing ──────────────────────────────────────────────────────────

  /** Every *.json in a folder, parsed; unreadable files are skipped. */
  private listFolderJson<T>(path: string, maxItems = 500): Observable<T[]> {
    return this.folderId(path).pipe(
      switchMap((folderId) => from(this.nodes.listNodeChildren(folderId, { maxItems } as any) as Promise<any>)),
      switchMap((res: any) => {
        const files: any[] = (res?.list?.entries || []).map((e: any) => e.entry).filter((n: any) => n.isFile && n.name?.endsWith('.json'));
        if (!files.length) {
          return of([] as T[]);
        }
        return forkJoin(
          files.map((f: any) =>
            this.readText(f.id).pipe(
              map((text): T | null => {
                try {
                  const parsed = JSON.parse(text) as T;
                  (parsed as any).id = f.id;
                  (parsed as any).nodeId = f.id;
                  return parsed;
                } catch {
                  return null;
                }
              }),
              catchError(() => of(null))
            )
          )
        ).pipe(map((items) => items.filter((i): i is T => i !== null)));
      }),
      catchError(() => of([] as T[]))
    );
  }

  private readJson<T>(path: string): Observable<T | null> {
    return this.nodeId(path, false).pipe(
      switchMap((id) => (id ? this.readText(id) : of(null))),
      map((text) => {
        if (!text) {
          return null;
        }
        try {
          return JSON.parse(text) as T;
        } catch {
          return null;
        }
      }),
      catchError(() => of(null))
    );
  }

  /** Overwrites (or creates) a JSON document at a Company-Home-relative path. */
  private writeJson(path: string, doc: unknown): Observable<void> {
    const json = JSON.stringify(doc, null, 2);
    return this.nodeId(path, true).pipe(
      switchMap((id) => (id ? from(this.nodes.updateNodeContent(id, json) as Promise<any>) : of(null))),
      map(() => undefined)
    );
  }

  private readText(nodeId: string): Observable<string> {
    return from(this.nodes.getNodeContent(nodeId) as Promise<any>).pipe(
      switchMap((data: any) => from(typeof data?.text === 'function' ? (data.text() as Promise<string>) : Promise.resolve(String(data ?? ''))))
    );
  }

  /**
   * The node id of a file inside Company Home, creating the folder chain and
   * the empty document on the way when `create` is set — which only ever
   * happens on an administrator's write.
   */
  private nodeId(path: string, create: boolean): Observable<string | null> {
    return from(this.nodes.getNode('-root-', { relativePath: path } as any) as Promise<any>).pipe(
      map((res: any) => res?.entry?.id as string),
      catchError(() => {
        if (!create) {
          return of(null);
        }
        const parts = path.split('/');
        const name = parts.pop() ?? 'data.json';
        const folder = parts.join('/');
        return this.folderId(folder).pipe(
          switchMap((folderId) => from(this.nodes.createNode(folderId, { name, nodeType: 'cm:content' } as any) as Promise<any>)),
          map((res: any) => res?.entry?.id as string),
          catchError(() => of(null))
        );
      })
    );
  }

  /** A Company-Home-relative path → node id, creating missing folders along the way. */
  private folderId(path: string): Observable<string> {
    const segments = path.split('/').filter(Boolean);
    const walk = (parentId: string, rest: string[], walked: string[]): Observable<string> => {
      if (!rest.length) {
        return of(parentId);
      }
      const next = [...walked, rest[0]].join('/');
      return from(this.nodes.getNode('-root-', { relativePath: next } as any) as Promise<any>).pipe(
        map((res: any) => res.entry.id as string),
        catchError(() =>
          from(this.nodes.createNode(parentId, { name: rest[0], nodeType: 'cm:folder' } as any) as Promise<any>).pipe(
            map((res: any) => res.entry.id as string)
          )
        ),
        switchMap((id) => walk(id, rest.slice(1), [...walked, rest[0]]))
      );
    };
    return walk('-root-', segments, []);
  }

  private fileName(name: string): string {
    const slug = (name || 'migration')
      .replace(/[^\p{L}\p{N}]+/gu, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60);
    return `${slug || 'migration'}-${Date.now().toString(36)}.json`;
  }
}
